import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

import { generateIdeasByGuidingQuestion } from '../services/Brainstorm/BrainstormService';

const MAX_IDEAS_PER_QUESTION = 38;

const useBrainstormStore = create(persist((set, get) => ({
    brainstorms: [],

    // operações de Brainstorm
    createBrainstorm: (projectId) => {
        const newBrainstorm = {
            id: uuidv4(),
            project: projectId,
            challenges: [],
            guiding_questions: [],
            ideas: [],
            groups: [],
            concepts: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            step: 1 // 1 = challenges, 2 = guiding questions, 3 = ideas, 4 = groups, 5 = concepts
        };

        set((state) => ({
            brainstorms: [...state.brainstorms, newBrainstorm]
        }));

        return newBrainstorm;
    },

    createBrainstormFromData: (projectId, data) => {
        const newBrainstorm = {
            id: uuidv4(),
            project: projectId,
            challenges: data.challenges || [],
            guiding_questions: data.guiding_questions || [],
            ideas: data.ideas || [],
            groups: data.groups || [],
            concepts: data.concepts || [],
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt || new Date().toISOString(),
            step: data.step || 1 // 1 = challenges, 2 = guiding questions, 3 = ideas, 4 = groups, 5 = concepts
        };

        set((state) => ({
            brainstorms: [...state.brainstorms, newBrainstorm]
        }));

        return newBrainstorm;
    },

    changeBrainstormStep: (brainstormId, step) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId ? { ...brainstorm, step } : brainstorm
            )
        }));
    },

    getBrainstormById: (brainstormId) => {
        const { brainstorms } = get();
        return brainstorms.find(brainstorm => brainstorm.id === brainstormId);
    },

    getBrainstormsByProject: (projectId) => {
        if (typeof projectId === 'string') {
            projectId = parseInt(projectId);
        }

        const { brainstorms } = get();
        const matchBrainstorms = brainstorms.filter(brainstorm => brainstorm.project === projectId);

        // Sort challenges by creation date (newest first)
        if (matchBrainstorms[0]) {
            matchBrainstorms[0].challenges.sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB - dateA; // Descending order (newest first)
            });
        }
        
        return matchBrainstorms;
    },

    updateBrainstorm: (brainstormId, updates) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? { 
                        ...brainstorm, 
                        ...updates, 
                        updatedAt: new Date().toISOString() 
                    }
                    : brainstorm
            )
        }));
    },

    deleteBrainstorm: (brainstormId) => {
        set((state) => ({
            brainstorms: state.brainstorms.filter(brainstorm => brainstorm.id !== brainstormId)
        }));
    },

    // operações de Challenge
    addChallenge: (brainstormId, challengeText) => {
        const newChallenge = {
            id: uuidv4(),
            text: challengeText,
            createdAt: new Date().toISOString(),
            selected: false,
        };

        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        challenges: [...brainstorm.challenges, newChallenge],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        return newChallenge;
    },

    updateChallenge: (brainstormId, challengeId, updates) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        challenges: brainstorm.challenges.map(challenge =>
                            challenge.id === challengeId
                                ? { ...challenge, ...updates }
                                : challenge
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteChallenge: (brainstormId, challengeId) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        challenges: brainstorm.challenges.filter(challenge => challenge.id !== challengeId),
                        // Remove questões norteadoras vinculadas ao desafio
                        guiding_questions: brainstorm.guiding_questions.filter(gq => gq.challenge_id !== challengeId),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    getChallengeById: (brainstormId, challengeId) => {
        const brainstorm = get().getBrainstormById(brainstormId);
        return brainstorm?.challenges.find(challenge => challenge.id === challengeId);
    },

    // operações de Guiding Questions
    addGuidingQuestion: (brainstormId, questionText, challengeId = '', selected = false) => {
        const newQuestion = {
            id: uuidv4(),
            text: questionText,
            challenge_id: challengeId,
            selected: selected,
            in_knowledge_base: true,
            createdAt: new Date().toISOString(),
            ideas: [],
        };

        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: [...brainstorm.guiding_questions, newQuestion],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        return newQuestion;
    },

    addMultipleGuidingQuestions: (brainstormId, questionsData) => {
        const newQuestions = questionsData.map(questionData => ({
            id: uuidv4(),
            text: questionData.text,
            challenge_id: questionData.challengeId || '',
            selected: questionData.selected || false,
            in_knowledge_base: questionData.in_knowledge_base || true,
            createdAt: new Date().toISOString(),
            ideas: [],
        }));

        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: [...brainstorm.guiding_questions, ...newQuestions],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        return newQuestions;
    },

    updateGuidingQuestion: (brainstormId, questionId, updates) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === questionId
                                ? { ...question, ...updates }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    toggleGuidingQuestionKnowledgeBase: (brainstormId, questionId) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === questionId
                                ? { 
                                    ...question, 
                                    in_knowledge_base: !question.in_knowledge_base 
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteGuidingQuestion: (brainstormId, questionId) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.filter(question => question.id !== questionId),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteMultipleGuidingQuestions: (brainstormId, questionIds) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.filter(question => !questionIds.includes(question.id)),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    getGuidingQuestionById: (brainstormId, questionId) => {
        const brainstorm = get().getBrainstormById(brainstormId);
        return brainstorm?.guiding_questions.find(question => question.id === questionId);
    },

    getGuidingQuestionsByChallenge: (brainstormId, challengeId) => {
        const brainstorm = get().getBrainstormById(brainstormId);
        const questions = brainstorm?.guiding_questions.filter(question => question.challenge_id === challengeId) || [];
        
        // Sort questions by creation date (newest first)
        return questions.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
        });
    },

    getIdeasByGuidingQuestion: (brainstormId, guidingQuestionId) => {
        const brainstorm = get().getBrainstormById(brainstormId);
        const guidingQuestion = brainstorm?.guiding_questions.find(question => question.id === guidingQuestionId);
        
        if (!guidingQuestion) {
            return [];
        }

        const ideas = guidingQuestion.ideas || [];
        
        // Sort ideas by creation date (newest first)
        return ideas.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
        });
    },

    getIdeaById: (ideaId) => {
        const { brainstorms } = get();
        
        for (const brainstorm of brainstorms) {
            for (const guidingQuestion of brainstorm.guiding_questions || []) {
                const idea = guidingQuestion.ideas?.find(idea => idea.id === ideaId);
                if (idea) {
                    return idea;
                }
            }
        }
        
        return null; // Idea not found
    },

    addIdeaToGuidingQuestion: (brainstormId, guidingQuestionId, ideaData) => {
        const state = get();
        const brainstorm = state.brainstorms.find(b => b.id === brainstormId);
        
        if (!brainstorm) {
            throw new Error('Brainstorm not found');
        }

        const guidingQuestion = brainstorm.guiding_questions.find(q => q.id === guidingQuestionId);
        
        if (!guidingQuestion) {
            throw new Error('Guiding question not found');
        }

        const currentIdeasCount = (guidingQuestion.ideas || []).length;
        
        if (currentIdeasCount >= MAX_IDEAS_PER_QUESTION) {
            throw new Error('Maximum limit of 38 ideas per guiding question has been reached');
        }

        const newIdea = {
            id: uuidv4(),
            title: ideaData.title || '',
            description: ideaData.description || '',
            createdAt: new Date().toISOString(),
            ...ideaData
        };

        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: [...(question.ideas || []), newIdea]
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        return newIdea;
    },

    addMultipleIdeasToGuidingQuestion: (brainstormId, guidingQuestionId, ideasData) => {
        const state = get();
        const brainstorm = state.brainstorms.find(b => b.id === brainstormId);
        
        if (!brainstorm) {
            throw new Error('Brainstorm not found');
        }

        const guidingQuestion = brainstorm.guiding_questions.find(q => q.id === guidingQuestionId);
        
        if (!guidingQuestion) {
            throw new Error('Guiding question not found');
        }

        const currentIdeasCount = (guidingQuestion.ideas || []).length;
        const remainingSlots = MAX_IDEAS_PER_QUESTION - currentIdeasCount;
        
        if (remainingSlots <= 0) {
            throw new Error('Maximum limit of 38 ideas per guiding question has been reached');
        }

        // Only add ideas up to the limit
        const ideasToAdd = ideasData.slice(0, remainingSlots);
        
        if (ideasToAdd.length < ideasData.length) {
            console.warn(`Only ${ideasToAdd.length} out of ${ideasData.length} ideas will be added to reach the maximum limit of 38 ideas per guiding question`);
        }

        const newIdeas = ideasToAdd.map(ideaData => ({
            id: uuidv4(),
            title: ideaData.title || '',
            description: ideaData.description || '',
            createdAt: new Date().toISOString(),
            ...ideaData
        }));

        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: [...(question.ideas || []), ...newIdeas]
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        return newIdeas;
    },

    updateIdeaInGuidingQuestion: (brainstormId, guidingQuestionId, ideaId, updates) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: question.ideas?.map(idea =>
                                        idea.id === ideaId
                                            ? { ...idea, ...updates }
                                            : idea
                                    ) || []
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    updateMultipleIdeasInGuidingQuestion: (brainstormId, guidingQuestionId, updates) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: question.ideas?.map(idea => ({
                                        ...idea,
                                        ...updates
                                    })) || []
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteIdeaFromGuidingQuestion: (brainstormId, guidingQuestionId, ideaId) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: question.ideas?.filter(idea => idea.id !== ideaId) || []
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteMultipleIdeas: (brainstormId, guidingQuestionId, ideaIds) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question =>
                            question.id === guidingQuestionId
                                ? {
                                    ...question,
                                    ideas: question.ideas?.filter(idea => !ideaIds.includes(idea.id)) || []
                                }
                                : question
                        ),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    generateInitialIdeas: async (brainstormId, projectId, keycloakToken, ideasNumber = 5) => {
        const brainstorm = get().getBrainstormById(brainstormId);
        if (!brainstorm) {
            throw new Error('Brainstorm not found');
        }

        const selectedGuidingQuestions = brainstorm.guiding_questions.filter(question => question.selected === true);
        
        if (selectedGuidingQuestions.length === 0) {
            console.warn('No selected guiding questions found');
            return [];
        }

        console.log(`Generating ideas for ${selectedGuidingQuestions.length} selected guiding questions`);

        const ideaGenerationPromises = selectedGuidingQuestions.map(async (guidingQuestion) => {
            try {
                // Get the challenge associated with this guiding question
                const challenge = brainstorm.challenges.find(c => c.id === guidingQuestion.challenge_id);
                const challengeText = challenge?.text || '';

                const apiResponse = await generateIdeasByGuidingQuestion(
                    projectId,
                    guidingQuestion.text,
                    challengeText,
                    ideasNumber,
                    null,
                    keycloakToken
                );

                // Extract ideas from API response
                const generatedIdeas = apiResponse?.response?.ideias || [];
                
                // Transform API response to our idea format
                const formattedIdeas = generatedIdeas.map((idea, index) => ({
                    id: uuidv4(),
                    title: idea.titulo_da_ideia || `Ideia ${index + 1}`,
                    description: idea.descricao_da_ideia || '',
                    createdAt: new Date().toISOString(),
                    source: 'api_generated',
                    selected: true,
                    guidingQuestionId: guidingQuestion.id
                }));

                return {
                    guidingQuestionId: guidingQuestion.id,
                    ideas: formattedIdeas
                };
            } catch (error) {
                console.error(`Error generating ideas for guiding question ${guidingQuestion.id}:`, error);
                return {
                    guidingQuestionId: guidingQuestion.id,
                    ideas: [],
                    error: error.message
                };
            }
        });

        // Wait for all API calls to complete
        const results = await Promise.all(ideaGenerationPromises);

        // 3. Update the store with all generated ideas
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question => {
                            const result = results.find(r => r.guidingQuestionId === question.id);
                            if (result) {
                                return {
                                    ...question,
                                    ideas: result.ideas
                                };
                            }
                            return question;
                        }),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));

        // 4. Return all generated ideas for the selected guiding questions
        const allGeneratedIdeas = results.reduce((acc, result) => {
            return [...acc, ...result.ideas];
        }, []);
        
        return {
            totalIdeas: allGeneratedIdeas.length,
            ideasByGuidingQuestion: results,
            allIdeas: allGeneratedIdeas
        };
    },

    // funções utilitárias
    clearAllBrainstorms: () => {
        set({ brainstorms: [] });
    },

    clearBrainstormsByProject: (projectId) => {
        set((state) => ({
            brainstorms: state.brainstorms.filter(brainstorm => brainstorm.project !== projectId)
        }));
    },

    deleteBrainstormByProjectId: (projectId) => {
        const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
        
        set((state) => ({
            brainstorms: state.brainstorms.filter(brainstorm => brainstorm.project !== numericProjectId)
        }));
    },

    // Ideas, Groups, and Concepts arrays para futura expansão
    addIdea: (brainstormId, idea) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        ideas: [...brainstorm.ideas, idea],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    addGroup: (brainstormId, group) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        groups: [...brainstorm.groups, group],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    addConcept: (brainstormId, concept) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        concepts: [...brainstorm.concepts, concept],
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    },

    deleteConcept: (brainstormId, ideaId) => {
        set((state) => ({
            brainstorms: state.brainstorms.map(brainstorm =>
                brainstorm.id === brainstormId
                    ? {
                        ...brainstorm,
                        guiding_questions: brainstorm.guiding_questions.map(question => ({
                            ...question,
                            ideas: question.ideas?.map(idea => {
                                if (idea.id === ideaId) {
                                    // Remove the concept property from the idea and mark it as deleted
                                    const { concept, ...ideaWithoutConcept } = idea;
                                    return { ...ideaWithoutConcept, conceptDeleted: true };
                                }
                                return idea;
                            }) || []
                        })),
                        updatedAt: new Date().toISOString()
                    }
                    : brainstorm
            )
        }));
    }
}), { 
    name: 'brainstorm-store',
    version: 1 
}));

export default useBrainstormStore;